import { useMemo, useState } from "react";
import { InterviewRowActions } from "../../components/interviewList/InterviewRowActions";
import { InterviewStatusBadge } from "../../components/interviewList/InterviewStatusBadge";
import {
  formatInterviewDate,
  formatInterviewTime,
} from "../../components/interviewList/schedule";
import {
  INTERVIEW_STATUS_FILTER_OPTIONS,
  resolveInterviewDisplayStatus,
} from "../../components/interviewList/status";
import { Button } from "../../components/ui/button";
import { useInterviews } from "../../hooks";

export function Interviews() {
  const { interviews, loading, error, refetch } = useInterviews();

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterSponsor, setFilterSponsor] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterInterviewer, setFilterInterviewer] = useState<string>("all");

  const sponsorOptions = useMemo(() => {
    const sponsors = [
      ...new Set(
        interviews
          .map((i) => i.sponsorName)
          .filter((name): name is string => name != null),
      ),
    ].sort();
    return [
      { value: "all", label: "すべて" },
      ...sponsors.map((s) => ({ value: s, label: s })),
    ];
  }, [interviews]);

  const interviewerOptions = useMemo(() => {
    const interviewers = [
      ...new Set(
        interviews
          .map((i) => i.interviewerEmail)
          .filter((email): email is string => email != null),
      ),
    ].sort();
    return [
      { value: "all", label: "すべて" },
      ...interviewers.map((email) => {
        const interview = interviews.find((i) => i.interviewerEmail === email);
        const name = interview?.interviewerName || email;
        return { value: email, label: `${name} (${email})` };
      }),
    ];
  }, [interviews]);

  const filteredAndSortedInterviews = useMemo(() => {
    let result = [...interviews];

    if (filterSponsor !== "all") {
      result = result.filter((i) => i.sponsorName === filterSponsor);
    }
    if (filterStatus !== "all") {
      result = result.filter(
        (i) => resolveInterviewDisplayStatus(i) === filterStatus,
      );
    }
    if (filterInterviewer !== "all") {
      result = result.filter((i) => i.interviewerEmail === filterInterviewer);
    }

    result.sort((a, b) => {
      const dateA = new Date(a.startTime).getTime();
      const dateB = new Date(b.startTime).getTime();
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    return result;
  }, [interviews, filterSponsor, filterStatus, filterInterviewer, sortOrder]);

  const hasActiveFilters =
    filterSponsor !== "all" ||
    filterStatus !== "all" ||
    filterInterviewer !== "all";

  const clearFilters = () => {
    setFilterSponsor("all");
    setFilterStatus("all");
    setFilterInterviewer("all");
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-[var(--color-bg-subtle-gray)] p-6">
      <div className="mx-auto max-w-[1200px]">
        <div className="mb-4">
          <nav className="flex items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
            <span className="font-medium text-[var(--color-text-primary)]">
              インタビュー
            </span>
          </nav>
        </div>

        <div className="mb-8">
          <div className="border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              インタビュー管理
            </h1>
            <p className="font-medium text-[var(--color-text-secondary)] text-sm">
              全インタビューの確認・管理
            </p>
          </div>
        </div>

        {!loading && interviews.length > 0 && (
          <div className="mb-6 rounded-[10px] border border-[#b3d9f0] bg-[var(--color-primary-50)] px-6 py-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-bold text-[var(--color-primary-500)] text-sm">
                絞り込み・並び替え
              </h3>
              <div className="flex items-center gap-2">
                <span className="font-medium text-[13px] text-[var(--color-primary-500)]">
                  {hasActiveFilters ? (
                    <>
                      {filteredAndSortedInterviews.length}件 /{" "}
                      {interviews.length}件
                    </>
                  ) : (
                    <>{interviews.length}件</>
                  )}
                </span>
                {hasActiveFilters && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="gap-1 text-[var(--color-primary-500)]"
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    クリア
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="mb-1.5 block font-semibold text-[var(--color-primary-500)] text-xs">
                  日程
                </label>
                <Button
                  variant="outline"
                  onClick={() =>
                    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                  }
                  className="w-full justify-between text-[13px]"
                >
                  {sortOrder === "desc" ? "新しい順" : "古い順"}
                  <span className="text-[var(--color-primary-500)]">
                    {sortOrder === "desc" ? "↓" : "↑"}
                  </span>
                </Button>
              </div>

              <div>
                <label className="mb-1.5 block font-semibold text-[var(--color-primary-500)] text-xs">
                  会社
                </label>
                <select
                  value={filterSponsor}
                  onChange={(e) => setFilterSponsor(e.target.value)}
                  className="h-10 w-full cursor-pointer rounded-md border border-[#b3d9f0] bg-white px-3 text-[13px] text-[var(--color-text-primary)]"
                >
                  {sponsorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block font-semibold text-[var(--color-primary-500)] text-xs">
                  ステータス
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="h-10 w-full cursor-pointer rounded-md border border-[#b3d9f0] bg-white px-3 text-[13px] text-[var(--color-text-primary)]"
                >
                  {INTERVIEW_STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block font-semibold text-[var(--color-primary-500)] text-xs">
                  インタビュアー
                </label>
                <select
                  value={filterInterviewer}
                  onChange={(e) => setFilterInterviewer(e.target.value)}
                  className="h-10 w-full cursor-pointer rounded-md border border-[#b3d9f0] bg-white px-3 text-[13px] text-[var(--color-text-primary)]"
                >
                  {interviewerOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-center p-12">
              <div className="flex flex-col items-center gap-4">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-border-default)] border-t-[var(--color-primary-500)]" />
                <p className="font-medium text-[var(--color-text-secondary)]">
                  読み込み中...
                </p>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-red-100)] bg-[var(--color-red-50)] p-6">
            <p className="font-medium text-[var(--color-red-600)]">
              インタビュー一覧の読み込みに失敗しました
            </p>
            <p className="mt-2 text-[var(--color-red-600)] text-sm">{error}</p>
          </div>
        ) : interviews.length === 0 ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col items-center justify-center p-12">
              <svg
                className="mb-4 h-12 w-12 text-[#9ca3af]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-[#9ca3af]">インタビューがありません</p>
            </div>
          </div>
        ) : filteredAndSortedInterviews.length === 0 ? (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="flex flex-col items-center justify-center p-12">
              <svg
                className="mb-4 h-12 w-12 text-[#9ca3af]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <p className="mb-4 text-[#9ca3af]">
                条件に一致するインタビューがありません
              </p>
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-[var(--color-primary-500)]"
              >
                フィルタをクリア
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
            <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
              <h2 className="font-bold text-2xl text-[var(--color-primary-500)]">
                一覧
              </h2>
            </div>
            <div className="overflow-x-auto p-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      日程
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      候補者
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      会社
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ターム
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      ステータス
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      インタビュアー
                    </th>
                    <th className="p-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedInterviews.map((interview) => {
                    return (
                      <tr
                        key={interview.interviewId}
                        className="border-[var(--color-border-default)] border-t hover:bg-[var(--color-bg-subtle-gray)]"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="h-4 w-4 text-[#9ca3af]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <div>
                              <div className="font-medium text-[var(--color-text-primary)] text-sm">
                                {formatInterviewDate(interview.startTime)}
                              </div>
                              <div className="text-[var(--color-text-secondary)] text-xs">
                                {formatInterviewTime(interview.startTime)} -{" "}
                                {formatInterviewTime(interview.endTime)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-[var(--color-text-primary)] text-sm">
                            {interview.candidateName ?? "-"}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="h-4 w-4 text-[#9ca3af]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            <span className="font-medium text-[var(--color-text-primary)] text-sm">
                              {interview.sponsorName}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="h-4 w-4 text-[#9ca3af]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <span className="text-[var(--color-text-secondary)] text-sm">
                              {interview.termTitle}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <InterviewStatusBadge
                            status={resolveInterviewDisplayStatus(interview)}
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <svg
                              className="h-4 w-4 text-[#9ca3af]"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            <div>
                              <div className="font-medium text-[var(--color-text-primary)] text-sm">
                                {interview.interviewerName ||
                                  interview.interviewerEmail ||
                                  "（名前未設定）"}
                              </div>
                              <div className="text-[var(--color-text-secondary)] text-xs">
                                {interview.interviewerEmail}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <InterviewRowActions
                            interview={interview}
                            onInterviewCancelled={refetch}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
